import React, { useEffect, useState, useRef } from 'react';

/**
 * UndoSendToast - Shows a countdown toast allowing users to cancel a sent email
 *
 * Props:
 * - isVisible: boolean - Whether the toast is visible
 * - delaySeconds: number - Total delay before email is actually sent (default: 5)
 * - onUndo: function - Callback when user clicks "Undo"
 * - onComplete: function - Callback when countdown completes (email should be sent)
 * - recipient: string - Email recipient to display
 */

const UndoSendToast = ({
  isVisible,
  delaySeconds = 5,
  onUndo,
  onComplete,
  recipient = '',
}) => {
  const [timeLeft, setTimeLeft] = useState(delaySeconds);
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (isVisible) {
      // Reset state when toast becomes visible
      setTimeLeft(delaySeconds);
      setProgress(100);
      startTimeRef.current = Date.now();

      // Update countdown every 100ms for smooth progress bar
      intervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        const remaining = Math.max(0, delaySeconds - elapsed);

        setTimeLeft(Math.ceil(remaining));
        setProgress((remaining / delaySeconds) * 100);

        if (remaining <= 0) {
          clearInterval(intervalRef.current);
          onComplete?.();
        }
      }, 100);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isVisible, delaySeconds, onComplete]);

  const handleUndo = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    onUndo?.();
  };

  if (!isVisible) return null;

  // Extract just the email or name for display
  const displayRecipient = recipient.includes('<')
    ? recipient.split('<')[0].trim().replace(/"/g, '')
    : recipient.split('@')[0];

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-slide-up">
      <div className="bg-gray-900 dark:bg-[#1A1A1A] rounded-2xl shadow-2xl overflow-hidden min-w-[320px] max-w-[400px]">
        {/* Progress bar at top */}
        <div className="h-1 bg-gray-700 dark:bg-[#333333]">
          <div
            className="h-full bg-brand transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="px-4 py-3 flex items-center justify-between gap-4">
          {/* Left side: message */}
          <div className="flex items-center gap-3">
            {/* Checkmark icon */}
            <div className="w-8 h-8 rounded-full bg-brand/20 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-brand"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <div className="flex flex-col">
              <span className="text-white text-sm font-medium">
                Sending to {displayRecipient}
              </span>
              <span className="text-gray-400 text-xs">
                {timeLeft}s remaining
              </span>
            </div>
          </div>

          {/* Right side: Undo button */}
          <button
            onClick={handleUndo}
            className="px-4 py-2 text-sm font-semibold text-brand hover:bg-brand/10 rounded-lg transition-colors"
          >
            Undo
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default UndoSendToast;
